// src/shared/hooks/useEscapeLayer.ts
// ─────────────────────────────────────────────────────────────────────────────
// Global Escape-key layer stack.
//
// Architecture: module-level LIFO stack + a single window `keydown` listener.
// Only the topmost active handler fires on each Escape press, giving correct
// "peel-off" behaviour for arbitrarily nested popups, overlays, and drawers
// without any Context provider.
//
// Usage:
//   useEscapeLayer(() => closeMyOverlay(), isOverlayOpen);
//
// SOLID compliance:
//   S — single responsibility: manages Escape dismiss only
//   O — open for extension: any component can register
//   D — dependency inversion: consumers depend on the hook abstraction
// DRY: one mechanism, zero per-component Escape boilerplate
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';

interface EscapeEntry {
  id: symbol;
  handler: () => void;
}

const stack: EscapeEntry[] = [];
let listenerBound = false;

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  if (stack.length === 0) return;
  if (e.defaultPrevented) return;

  e.preventDefault();
  stack[stack.length - 1].handler();
}

function attach() {
  if (listenerBound) return;
  window.addEventListener('keydown', onKeyDown);
  listenerBound = true;
}

function detach() {
  if (stack.length > 0) return;
  window.removeEventListener('keydown', onKeyDown);
  listenerBound = false;
}

/**
 * Register an Escape-key dismiss handler on the global layer stack.
 *
 * @param handler  Called when Escape is pressed and this is the topmost layer.
 * @param active   When false the handler is removed from the stack (default `true`).
 */
export function useEscapeLayer(handler: () => void, active = true) {
  const idRef = useRef(Symbol());
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!active) return;

    const entry: EscapeEntry = {
      id: idRef.current,
      handler: () => handlerRef.current(),
    };
    stack.push(entry);
    attach();

    return () => {
      const idx = stack.findIndex((e) => e.id === entry.id);
      if (idx >= 0) stack.splice(idx, 1);
      detach();
    };
  }, [active]);
}
